import { leadInPlainText } from "@/lib/lead-in-html"
import {
  AUTHORING_QUESTION_TYPES,
  type AuthoringQuestionType,
} from "@/lib/library-authoring"

import { QUALITY_RATIONALE_EMPTY_MARK } from "./run-checks"
import { DIFFICULTY_TAG_LABEL, normalizeDifficultyTag } from "./suggest-tags"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"
import { VARIATION_ENTIRE_SET_CHECK_ID } from "./variation-ask"

export interface QualitySuggestionLine {
  text: string
  replaces?: string
  optionId?: string
  isNew?: boolean
  isKept?: boolean
}

function plain(value: string | undefined): string {
  return leadInPlainText(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
}

function realCurrent(value: string | undefined): string | undefined {
  const text = plain(value)
  if (!text || text === QUALITY_RATIONALE_EMPTY_MARK) return undefined
  return text
}

function optionLetter(snapshot: QuestionQualitySnapshot, optionId: string): string {
  const index = snapshot.options.findIndex((option) => option.id === optionId)
  if (index < 0) return optionId.toUpperCase()
  return String.fromCharCode(65 + index)
}

function isStemOnlyAlternate(alternate: QuestionQualityAlternate): boolean {
  const patch = alternate.patch
  if (!patch?.leadIn) return false
  return (
    !patch.optionTexts?.length &&
    !patch.optionText &&
    !patch.addOptions?.length &&
    !patch.referencesHtml &&
    !patch.referenceFileName
  )
}

function isEntireSetPreview(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): boolean {
  const patch = alternate.patch
  if (!patch?.leadIn || !patch.optionTexts?.length) return false
  return (
    check.id === VARIATION_ENTIRE_SET_CHECK_ID ||
    alternate.id.startsWith("variation-entire-set")
  )
}

function contextOptionLines(
  snapshot: QuestionQualitySnapshot,
): QualitySuggestionLine[] {
  return snapshot.options.map((option, index) => {
    const line: QualitySuggestionLine = {
      text: plain(option.text),
      optionId: String.fromCharCode(65 + index),
    }
    if (option.isCorrect) line.isKept = true
    return line
  })
}

function entireSetOptionLines(
  snapshot: QuestionQualitySnapshot,
  alternate: QuestionQualityAlternate,
): QualitySuggestionLine[] {
  const patch = alternate.patch
  if (!patch?.optionTexts?.length) return []
  const patchById = new Map(
    patch.optionTexts.map((row) => [row.id, row]),
  )
  return snapshot.options.map((option, index) => {
    const row = patchById.get(option.id)
    const line: QualitySuggestionLine = {
      text: plain(row?.text ?? option.text),
      optionId: String.fromCharCode(65 + index),
    }
    const from = row?.from ? plain(row.from) : undefined
    if (from && from !== line.text) {
      line.replaces = from
    } else if (option.isCorrect) {
      line.isKept = true
    }
    return line
  })
}

/** Suggested option wordings to show on the card. Empty for stem or setting recs. */
export function qualitySuggestionLines(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
  snapshot?: QuestionQualitySnapshot,
): QualitySuggestionLine[] {
  const patch = alternate.patch
  if (!patch) return []
  const current = realCurrent(check.currentText ?? check.markText)
  if (patch.type) {
    const suggested =
      AUTHORING_QUESTION_TYPES.find((row) => row.value === patch.type)
        ?.label ?? patch.type
    const line: QualitySuggestionLine = { text: suggested }
    if (current) line.replaces = current
    return [line]
  }
  if (patch.bloom) {
    const line: QualitySuggestionLine = { text: plain(patch.bloom) }
    if (current) line.replaces = current
    return [line]
  }
  if (patch.difficulty) {
    const tag = normalizeDifficultyTag(patch.difficulty)
    const suggested = tag ? DIFFICULTY_TAG_LABEL[tag] : plain(patch.difficulty)
    const line: QualitySuggestionLine = { text: suggested }
    if (current) line.replaces = current
    return [line]
  }

  if (snapshot && isEntireSetPreview(check, alternate)) {
    const lines: QualitySuggestionLine[] = []
    const stemText = plain(patch.leadIn)
    if (stemText) {
      lines.push({
        text: stemText,
        replaces:
          check.field === "leadIn" ? current : undefined,
      })
    }
    lines.push(...entireSetOptionLines(snapshot, alternate))
    return lines
  }

  const lines: QualitySuggestionLine[] = []
  const push = (
    textValue: string | undefined,
    replaces?: string,
    optionId?: string,
    isNew = false,
  ) => {
    const text = plain(textValue)
    if (!text) return
    const from = replaces
    const note = from && from !== text ? from : undefined
    const line: QualitySuggestionLine = { text }
    if (note) line.replaces = note
    else if (from && from === text) line.isKept = true
    if (optionId) line.optionId = optionId
    if (isNew) line.isNew = true
    lines.push(line)
  }

  if (patch.leadIn) {
    push(
      plain(patch.leadIn),
      check.field === "leadIn" ? current : undefined,
    )
  }
  if (patch.optionText) push(patch.optionText, current, check.optionId)
  if (patch.optionTexts?.length) {
    const single = patch.optionTexts.length === 1
    for (const row of patch.optionTexts) {
      push(
        row.text,
        realCurrent(row.from) ?? (single ? current : undefined),
        snapshot ? optionLetter(snapshot, row.id) : row.id,
      )
    }
  }
  if (patch.addOptions?.length) {
    for (const text of patch.addOptions) push(text, undefined, undefined, true)
  }
  if (patch.referencesHtml) {
    push(
      plain(patch.referencesHtml.replace(/<[^>]+>/g, " ")),
      check.field === "references" ? current : undefined,
    )
  }
  if (patch.referenceFileName) {
    push(patch.referenceFileName)
  }
  if (
    snapshot &&
    isStemOnlyAlternate(alternate) &&
    (snapshot.type === "mcq_single" ||
      snapshot.type === "mcq_multiple" ||
      snapshot.type === "true_false") &&
    snapshot.options.length > 0
  ) {
    lines.push(...contextOptionLines(snapshot))
  }
  return lines
}
